import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { xano, type CompanySettings } from "@/lib/xano";
import { useCompany } from "@/lib/company";
import { PageHeader, LoadingState, ErrorState, EmptyState } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { SelectNative } from "@/components/ui/select-native";
import { Button } from "@/components/ui/button";
import { Building2, Check } from "lucide-react";
import { BillingSection } from "@/components/BillingSection";

type Field = {
  key: keyof CompanySettings;
  label: string;
  description: string;
  options: { value: string; label: string }[];
};

const FIELDS: Field[] = [
  {
    key: "product_sync",
    label: "Product sync & HS write-back",
    description:
      "Off keeps your Shopify catalogue untouched. Preview suggests HS codes for you to verify. Live writes approved HS codes back to Shopify.",
    options: [
      { value: "off", label: "Off" },
      { value: "preview", label: "Preview (suggest only)" },
      { value: "live", label: "Live (write back)" },
    ],
  },
  {
    key: "order_import",
    label: "Order import",
    description:
      "Off pulls no orders. On imports and classifies your orders read-only, so nothing posts to your books until you allow it below.",
    options: [
      { value: "off", label: "Off" },
      { value: "on", label: "On (import & classify)" },
    ],
  },
  {
    key: "transaction_posting",
    label: "Transaction posting",
    description:
      "Preview computes the journals but never posts. Live posts approved transactions into your connected accounting provider.",
    options: [
      { value: "off", label: "Off" },
      { value: "preview", label: "Preview (compute only)" },
      { value: "live", label: "Live (post to books)" },
    ],
  },
  {
    key: "checkout_mode",
    label: "Checkout duties",
    description:
      "Off shows nothing at checkout. Display shows the landed-cost estimate. Collect charges duties and import VAT at checkout (requires an eligible Shopify plan).",
    options: [
      { value: "off", label: "Off" },
      { value: "display", label: "Display estimate" },
      { value: "collect", label: "Collect at checkout" },
    ],
  },
];

export default function Settings() {
  const { active, activeToken } = useCompany();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<CompanySettings | null>(null);
  const [saved, setSaved] = useState(false);

  const q = useQuery({
    queryKey: ["settings", activeToken],
    queryFn: () => xano.dashboard.settings.get(activeToken!),
    enabled: !!activeToken,
  });

  useEffect(() => {
    if (q.data) setDraft(q.data);
  }, [q.data]);

  const mutation = useMutation({
    mutationFn: (body: Partial<CompanySettings>) =>
      xano.dashboard.settings.set(activeToken!, body),
    onSuccess: (data) => {
      setDraft(data);
      qc.invalidateQueries({ queryKey: ["settings", activeToken] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  if (!activeToken) {
    return (
      <>
        <PageHeader title="Settings" />
        <EmptyState icon={Building2} title="No company yet" />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Controls"
        description={
          active
            ? `${active.name} — these toggles are enforced server-side. Off and Preview never touch your store or books.`
            : undefined
        }
      />

      {q.isLoading || !draft ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : (
        <div className="max-w-2xl space-y-4">
          {FIELDS.map((f) => (
            <Card key={f.key}>
              <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="sm:max-w-md">
                  <div className="font-medium">{f.label}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{f.description}</p>
                </div>
                <SelectNative
                  className="h-9 w-full sm:w-56"
                  value={draft[f.key] ?? f.options[0].value}
                  onChange={(e) =>
                    setDraft((d) => (d ? { ...d, [f.key]: e.target.value } : d))
                  }
                >
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </SelectNative>
              </CardContent>
            </Card>
          ))}

          <div className="flex items-center gap-3">
            <Button onClick={() => draft && mutation.mutate(draft)} disabled={mutation.isPending}>
              {mutation.isPending ? "Saving…" : "Save controls"}
            </Button>
            {saved && (
              <span className="flex items-center gap-1 text-sm text-emerald-600">
                <Check className="h-4 w-4" /> Saved
              </span>
            )}
            {mutation.isError && (
              <span className="text-sm text-destructive">
                {mutation.error instanceof Error ? mutation.error.message : "Save failed."}
              </span>
            )}
          </div>
        </div>
      )}

      <BillingSection />
    </>
  );
}
